import React, { useMemo } from 'react';
import ReactDOM from 'react-dom';
import App from './pages';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { useTranslation } from 'react-i18next';
import { Router } from 'react-router';
import { ConfigProvider } from 'antd';
import enGB from 'antd/es/locale/en_GB';
import deDE from 'antd/es/locale/de_DE';
import esES from 'antd/es/locale/es_ES';
import csCZ from 'antd/es/locale/cs_CZ';
import jaJP from 'antd/es/locale/ja_JP';
import skSK from 'antd/es/locale/sk_SK';
import itIT from 'antd/es/locale/it_IT';
import zhTW from 'antd/es/locale/zh_TW';
import moment from 'moment';
import { config as fontAwesomeConfig } from '@fortawesome/fontawesome-svg-core';
import '@assets/fonts';
import 'moment/locale/en-gb';
import 'moment/locale/de';
import 'moment/locale/es';
import 'moment/locale/cs';
import 'moment/locale/ja';
import 'moment/locale/sk';
import 'moment/locale/it';
import 'moment/locale/zh-tw';
import TokenPool from './redux/TokenPool';
import OrganisationDetailsCache from '@utils/organisationDetailsCache';
import { initialise as initialiseAnalytics, trackApplicationEvent } from './analytics';
import createStore from './store';
import * as serviceWorker from './serviceWorker';
import './i18n';
import './style.less';

// FontAwesome SVG Core by default adds its own CSS style tags at runtime
// which breaks with a strict CSP.
// As per instructions at https://docs.fontawesome.com/web/dig-deeper/security/ :
// To fix this the node_modules/fontawesome-svg-core/styles.css file needs to be copied
// and manually imported to be included in the build.
// In addition we need to set the fontAwesomeConfig.autoAddCss property to false.
import './theme/fontawesome-svg-styles.css';
// Make sure this is before any other `fontawesome` API calls
fontAwesomeConfig.autoAddCss = false;

const history = createBrowserHistory();

const tokens = new TokenPool();
const organisationDetailsCache = new OrganisationDetailsCache(tokens);

const store = createStore({ history, tokens, organisationDetailsCache });

initialiseAnalytics();
trackApplicationEvent('Loading');

// maps i18next ISO language codes to antd locale codes
const getLocale = lang => {
  switch (true) {
    case lang.startsWith('en'):
      return enGB;
    case lang.startsWith('de'):
      return deDE;
    case lang.startsWith('es'):
      return esES;
    case lang.startsWith('cs'):
      return csCZ;
    case lang.startsWith('ja'):
      return jaJP;
    case lang.startsWith('sk'):
      return skSK;
    case lang.startsWith('it'):
      return itIT;
    case lang.startsWith('zh'):
      return zhTW;
    default:
      return enGB;
  }
};

const Application = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const locale = useMemo(() => getLocale(language), [language]);
  // const locale = enGB;

  moment.locale(language);

  return (
    <Provider store={store}>
      <ConfigProvider locale={locale} wave={{ disabled: true }}>
        <Router history={history}>
          <App />
        </Router>
      </ConfigProvider>
    </Provider>
  );
};

ReactDOM.render(<Application />, document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
